class ProductSizeGuideDrawer extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('.size-guide-panel__dialog');
    this.backdrop = this.querySelector('.size-guide-panel__backdrop');
    this.closeButtons = this.querySelectorAll('[data-size-guide-close]');
    this.triggerLinks = [];
    this.lastFocusedElement = null;
    this.closeTimer = null;
    this.isClosing = false;
    this.originalBodyPaddingRight = '';
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.finishClose = this.finishClose.bind(this);
  }

  connectedCallback() {
    this.portalOverlay();
    this.triggerLinks = this.getTriggerLinks();
    this.triggerLinks.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    if (window.eventBus) window.eventBus.on('close:all:drawers', this.close);
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    this.isClosing = false;
    this.dialog.hidden = true;
    this.backdrop.hidden = true;
    this.unlockPageScroll();
    this.triggerLinks.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.close));
    document.removeEventListener('keydown', this.handleKeydown);
    if (window.eventBus) window.eventBus.off('close:all:drawers', this.close);
    this.backdrop.remove();
    this.dialog.remove();
  }

  portalOverlay() {
    /* Avoid the product section's stacking context so the dialog sits over navigation. */
    document.body.append(this.backdrop, this.dialog);
  }

  getTriggerLinks() {
    return [...document.querySelectorAll('[data-size-guide-link]')]
      .filter((trigger) => trigger.getAttribute('aria-controls') === this.id);
  }

  handleTriggerClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  lockPageScroll() {
    document.body.classList.add('size-guide-panel-open');

    /* Modern engines reserve the gutter through CSS. This covers older ones. */
    if (window.CSS && window.CSS.supports('scrollbar-gutter: stable')) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth <= 0) return;

    this.originalBodyPaddingRight = document.body.style.paddingRight;
    const currentPadding = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
    document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
  }

  unlockPageScroll() {
    document.body.classList.remove('size-guide-panel-open');
    document.body.style.paddingRight = this.originalBodyPaddingRight;
    this.originalBodyPaddingRight = '';
  }

  open(trigger) {
    if (this.isOpen() && !this.isClosing) return;
    window.clearTimeout(this.closeTimer);
    this.isClosing = false;
    delete this.dialog.dataset.sizeGuideClosing;
    delete this.backdrop.dataset.sizeGuideClosing;
    if (window.eventBus) window.eventBus.emit('close:all:drawers', this);

    this.lastFocusedElement = trigger || document.activeElement;
    this.backdrop.hidden = false;
    this.dialog.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    this.triggerLinks.forEach((link) => link.setAttribute('aria-expanded', 'true'));
    this.lockPageScroll();

    requestAnimationFrame(() => this.dialog.focus());
    document.addEventListener('keydown', this.handleKeydown);
  }

  close() {
    if (!this.isOpen() || this.isClosing) return;
    this.isClosing = true;
    this.setAttribute('aria-hidden', 'true');
    this.triggerLinks.forEach((link) => link.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.handleKeydown);

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishClose();
      return;
    }

    this.dialog.dataset.sizeGuideClosing = '';
    this.backdrop.dataset.sizeGuideClosing = '';
    this.closeTimer = window.setTimeout(this.finishClose, 220);
  }

  finishClose() {
    this.dialog.hidden = true;
    this.backdrop.hidden = true;
    delete this.dialog.dataset.sizeGuideClosing;
    delete this.backdrop.dataset.sizeGuideClosing;
    this.isClosing = false;
    this.unlockPageScroll();

    if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
      this.lastFocusedElement.focus();
    }
  }

  isOpen() {
    return this.getAttribute('aria-hidden') === 'false';
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...this.dialog.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('size-guide-panel')) {
  customElements.define('size-guide-panel', ProductSizeGuideDrawer);
}
